import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, SearchIcon } from 'lucide-react';
import { PageHero } from '../components/layout/PageHero';
import { EmptyState } from '../components/ui/EmptyState';
import { policies } from '../data/policies';
import { articles } from '../data/articles';
import { events } from '../data/events';
import { facts, faqs } from '../data/faqs';
type Result = {
  label: string;
  category: string;
  description: string;
  to: string;
};
const index: Result[] = [...policies.map(policy => ({
  label: policy.title,
  category: 'Policy',
  description: policy.summary,
  to: `/agenda/${policy.slug}`
})), ...articles.map(article => ({
  label: article.title,
  category: article.category,
  description: article.standfirst,
  to: `/news/${article.slug}`
})), ...events.map(event => ({
  label: event.title,
  category: 'Event',
  description: `${event.date} · ${event.city}, ${event.state}`,
  to: `/events/${event.slug}`
})), ...facts.map(fact => ({
  label: fact.claim,
  category: 'Fact',
  description: fact.position,
  to: `/facts/${fact.slug}`
})), ...faqs.map(faq => ({
  label: faq.question,
  category: 'FAQ',
  description: faq.answer,
  to: `/faq#${faq.id}`
})), {
  label: 'Adebayo S. Bello',
  category: 'Candidate',
  description: 'Biography, timeline, values and campaign gallery.',
  to: '/candidate'
}, {
  label: 'Media centre',
  category: 'Media',
  description: 'Official portraits, biography, brand assets and press contacts.',
  to: '/media'
}];
const categories = ['All', 'Policy', 'Event', 'Fact', 'FAQ', 'Candidate', 'Media'];
export function Search() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const results = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return [];
    return index.filter(item => {
      const matchesCategory = category === 'All' || item.category === category || category === 'Policy' && item.category === 'Policy';
      return matchesCategory && (item.label.toLowerCase().includes(term) || item.description.toLowerCase().includes(term));
    });
  }, [query, category]);
  return <>
      <PageHero eyebrow="Search" title="Find anything on this site." intro="Policies, news, speeches, events, clarifications and campaign materials." crumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'Search'
    }]} data-id="element-1351" data-mp="3ggfhfw27wquk" />

      <section className="bg-sand-50 py-14 lg:py-20" aria-label="Search the website" data-id="element-1352" data-mp="32gejoph91zjb">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10" data-id="element-1353" data-mp="2xpemrk84rhgt">
          <div className="relative" data-id="element-1354" data-mp="2xn1uad4qxopi">
            <label htmlFor="site-search" className="sr-only" data-id="element-1355" data-mp="29zag13znb1ll">
              Search the website
            </label>
            <SearchIcon aria-hidden className="pointer-events-none absolute left-5 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" data-id="element-1356" data-mp="2518yinhvbac8" />
            <input id="site-search" type="search" autoFocus value={query} onChange={event => setQuery(event.target.value)} placeholder="Search policies, news, events, facts…" className="w-full rounded-lg border border-sand-300 bg-white py-4 pl-14 pr-4 text-[17px] text-ink placeholder:text-slate-500/70 focus:border-green-600 focus:outline-none" data-id="element-1357" data-mp="p8wpwzj0ztg0" />
          </div>

          <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Filter results" data-id="element-1358" data-mp="185wspjp74c48">
            {categories.map(item => {
            const active = category === item;
            return <button key={item} type="button" aria-pressed={active} onClick={() => setCategory(item)} className={`rounded-lg border px-3.5 py-2 text-[14px] font-medium transition-colors duration-150 ease-emphasis ${active ? 'border-green-600 bg-green-600 text-white' : 'border-sand-300 bg-white text-slate-600 hover:border-slate-500/50 hover:text-ink'}`} data-id="element-1359" data-mp="z95nohso1mxi">
                  {item}
                </button>;
          })}
          </div>

          <div className="mt-8" data-id="element-1360" data-mp="lzncqlar1so8">
            {!query.trim() ? <div className="rounded-media border border-sand-200 bg-white p-8" data-id="element-1361" data-mp="hzcw8epbzjv4">
                <h2 className="font-display text-[20px] text-ink" data-id="element-1362" data-mp="hx8w87ujz504">Popular searches</h2>
                <ul className="mt-4 flex flex-wrap gap-2" data-id="element-1363" data-mp="d23bhccvzwdm">
                  {['security', 'jobs', 'power', 'education', 'volunteer', 'manifesto'].map(term => <li key={term} data-id="element-1364" data-mp="2xriwtrtc6svv">
                        <button type="button" onClick={() => setQuery(term)} className="rounded-lg border border-sand-300 px-3.5 py-2 text-[14px] text-slate-600 transition-colors duration-150 ease-emphasis hover:border-green-600 hover:text-green-700" data-id="element-1365" data-mp="2sref5ygozs42">
                          {term}
                        </button>
                      </li>)}
                </ul>
              </div> : results.length === 0 ? <EmptyState title="No results found" description={`Nothing on the site matches “${query}”. Try a broader term, or browse the agenda.`} actionLabel="Clear search" onAction={() => setQuery('')} data-id="element-1366" data-mp="31fs5pamrsusv" /> : <>
                <p className="text-[14px] text-slate-500" role="status" data-id="element-1367" data-mp="1gxua5yxt6chb">
                  {results.length} {results.length === 1 ? 'result' : 'results'} for “{query}”
                </p>
                <ul className="mt-4 divide-y divide-sand-300 border-y border-sand-300" data-id="element-1368" data-mp="1lyrzqwl5e8su">
                  {results.map(result => <li key={`${result.category}-${result.label}`} data-id="element-1369" data-mp="18xxzuz7qwq7s">
                      <Link to={result.to} className="group flex items-start gap-5 py-5" data-id="element-1370" data-mp="1nufafo8420ta">
                        <span className="mt-1 w-24 shrink-0 text-[11px] font-bold uppercase tracking-[0.1em] text-green-700" data-id="element-1371" data-mp="1ns2kans78k55">
                          {result.category}
                        </span>
                        <span className="flex-1" data-id="element-1372" data-mp="1wsqzvmx8qsk5">
                          <span className="block font-display text-[19px] leading-snug text-ink" data-id="element-1373" data-mp="1ruz9wyg5pgil">
                            {result.label}
                          </span>
                          <span className="mt-1 block text-[14px] leading-relaxed text-slate-600" data-id="element-1374" data-mp="21labwfga5fmu">
                            {result.description}
                          </span>
                        </span>
                        <ArrowRightIcon aria-hidden className="mt-2 h-4 w-4 shrink-0 text-green-600 transition-transform duration-200 ease-emphasis group-hover:translate-x-1" data-id="element-1375" data-mp="2bksuoskgevdb" />
                      </Link>
                    </li>)}
                </ul>
              </>}
          </div>
        </div>
      </section>
    </>;
}